import React from "react"
import { Link, graphql, useStaticQuery, navigate } from "gatsby"
import { Header } from "@organic-return/foundation-react/src/components/Header/Header"
import { INavItem } from "@organic-return/foundation-react/src/components/Header/Nav"
import { ILinkFunc } from "@organic-return/foundation-react/src/lib/util"
import { SubNavGroup } from "@organic-return/foundation-react/src/components/Header/SubNav"
import { EHeaderLayout } from "@organic-return/foundation-react/src/components/Header/Header"
import { ESocialChannel, ISocialLink } from "@organic-return/foundation-react/src/lib"
import { useSiteContext } from "./SiteContext"
import { ISiteMenuItem } from "../../lib/utils"

const SiteHeader = ({ languageCode, isMultiLanguage, ...headerProps }: any) => {
    // graphql query to fetch our sitemap & header data
    const data = useStaticQuery(graphql`
        query {
          configuration: drupalSiteConfiguration {
            layout
            icon {
              url
            }
            listingsSearchPath
            siteName
            logo {
              width
              height
              alt
              url
            }
            mobileLogo {
              width
              height
              alt
              url
            }
            addSocialLinksToNav
            socialLinks {
              facebook {
                url
                title
              }
              instagram {
                url
                title
              }
              linkedIn {
                url
                title
              }
              twitter {
                url
                title
              }
              youTube {
                url
                title
              }
              vimeo {
                url
                title
              }
              pinterest {
                url
                title
              }
            }
          }
        }
  `)
    const siteContext = useSiteContext();

    let navItems: INavItem[] = []

    const linkFunc: ILinkFunc = ({ href, className, children, key, onClick }) => (
        <Link to={href ?? ""} className={className} key={key} data-text={key} onClick={onClick}>
            {children}
        </Link>
    )

    const siteMenuItems = siteContext.siteMenuItems ?? []
    siteMenuItems.forEach((node: ISiteMenuItem, index: any) => {
        if (node.isFolder) {
            let topSng: SubNavGroup = {
                links: [],
            }
            let item: INavItem = {
                label: node.menuText,
                subNav: {
                    subNavGroups: [],
                    linkFunc: linkFunc,
                },
            }
            node.children?.forEach((node, index) => {
                let sng: SubNavGroup = {
                    links: [],
                }
                if (node.isFolder || node.children) {
                    sng.groupTitle = node.menuText
                    /**
                     * If node is not a folder, it must be a link with children
                     */
                    sng.isLinkTitle = !node.isFolder
                    sng.titleLink = !node.isFolder && node.path || ""
                    node.children?.forEach((node, index) => {
                        sng.links.push({
                            label: node.menuText,
                            url: node.path || "",
                        })
                    })
                    item.subNav?.subNavGroups.push(sng)
                } else {
                    topSng.links.push({
                        label: node.menuText,
                        url: node.path || "",
                    })
                }
            })
            if (topSng.links.length > 0) item.subNav?.subNavGroups.push(topSng)
            navItems.push(item)
        } else {
            navItems.push({
                label: node.menuText,
                url: node.path || "",
            })
        }
    })

    const getSearchPath = (searchPath: any, layout: string, location: any) => {        
        if (searchPath) {
            if (location == "header" && EHeaderLayout[layout as keyof typeof EHeaderLayout] == EHeaderLayout.side) {
                return {
                    searchPath: searchPath,
                }
            } else if (
                location == "nav" && EHeaderLayout[layout as keyof typeof EHeaderLayout] !== EHeaderLayout.side
            ) {
                return {
                    searchPath: searchPath,
                }
            }
        }
        return null
    }

    const getLogo = (
        logo: any,
        mobileLogo: any,
        layout: string,
        siteName: string
    ) => {
        if (EHeaderLayout[layout as keyof typeof EHeaderLayout] !== EHeaderLayout.side) {
            return {
                url: logo?.url,
                dimensions: [logo?.width, logo?.height],
                caption: siteName,
                sources: mobileLogo?.url && [
                    {
                        srcSet: `${mobileLogo.url}`,
                        media: `(max-width: 1023px)`,
                    },
                ],
            }
        }
        return {
            url: logo.url,
            caption: siteName,
            sources: [
                {
                    srcSet: `${mobileLogo.url}`,
                    media: `(max-width: 768px)`,
                },
            ],
        }
    }

    const configuration = data.configuration

    const sortByItemOrder = (a: any, b: any) => a.properties.itemOrder - b.properties.itemOrder

    let socials: ISocialLink[] = []    
    if (data.configuration.addSocialLinksToNav) {
        data.configuration?.socialLinks?.facebook &&
            socials.push({
                channel: ESocialChannel.Facebook,
                text:
                    data.configuration.socialLinks.facebook.title ||
                    data.configuration.siteName + " on Facebook",
                url: data.configuration.socialLinks.facebook.url,
                target: "__blank",
            })
        data.configuration?.socialLinks?.instagram &&
            socials.push({
                channel: ESocialChannel.Instagram,
                text:
                    data.configuration.socialLinks.instagram.title ||
                    data.configuration.siteName + " on Instagram",
                url: data.configuration.socialLinks.instagram.url,
                target: "__blank",
            })
        data.configuration?.socialLinks?.twitter &&
            socials.push({
                channel: ESocialChannel.Twitter,
                text:
                    data.configuration.socialLinks.twitter.title ||
                    data.configuration.siteName + " on Twitter",
                url: data.configuration.socialLinks.twitter.url,
                target: "__blank",
            })
        data.configuration?.socialLinks?.youTube &&
            socials.push({
                channel: ESocialChannel.YouTube,
                text:
                    data.configuration.socialLinks.youTube.title ||
                    data.configuration.siteName + " on YouTube",
                url: data.configuration.socialLinks.youTube.url,
                target: "__blank",
            })
        data.configuration?.socialLinks?.vimeo &&
            socials.push({
                channel: ESocialChannel.Vimeo,
                text:
                    data.configuration.socialLinks.vimeo.title ||
                    data.configuration.siteName + " on Vimeo",
                url: data.configuration.socialLinks.vimeo.url,
                target: "__blank",
            })
        data.configuration?.socialLinks?.pinterest &&
            socials.push({
                channel: ESocialChannel.Pinterest,
                text:
                    data.configuration.socialLinks.pinterest.title ||
                    data.configuration.siteName + " on Pinterest",
                url: data.configuration.socialLinks.pinterest.url,
                target: "__blank",
            })
        data.configuration?.socialLinks?.linkedIn &&
            socials.push({
                channel: ESocialChannel.LinkedIn,
                text:
                    data.configuration.socialLinks.linkedIn.title ||
                    data.configuration.siteName + " on LinkedIn",
                url: data.configuration.socialLinks.linkedIn.url,
                target: "__blank",
            })
    }   

    let navProps = {}
    if (headerProps.navProps) {
        navProps = headerProps.navProps
        delete headerProps.navProps
    }
    
    return (
        <Header
            linkFunc={linkFunc}
            layout={EHeaderLayout[configuration?.layout]}
            logo={getLogo(
                configuration?.logo,
                configuration?.mobileLogo,
                configuration?.layout,
                configuration?.siteName
            )}
            absolute={true}
            skipLinkTarget={"#main"}
            navProps={{
                navItems: navItems,
                searchText: "MLS #, Address, Keywords...",
                onSubmit: async filters => {
                    const params = new URLSearchParams({
                        filters: JSON.stringify(filters),
                    })
                    await navigate(
                        configuration.listingsSearchPath + "#" + params.toString()
                    )
                },
                ...getSearchPath(
                    configuration?.listingsSearchPath,
                    configuration?.layout,
                    "nav"
                ),
                ...navProps
            }}
            {...getSearchPath(
                configuration?.listingsSearchPath,
                configuration?.layout,
                "header"
            )}
            socialMediaLinks={socials}
            {...headerProps}
        />
    )
}
export default SiteHeader